// The phone's Back for a Sheet that asks for it (Sheet.svelte's `history`).
//
// While such a sheet is open it holds one entry in the browser's history, so
// Back closes the sheet instead of leaving the page. Every other way out
// takes the entry back off, so a closed sheet leaves the history as it found
// it and the next Back leaves the page as it would have. The entry carries no
// address: the page's own stays as it was.
//
// Sheets stack, so the entries do too: Back closes the top sheet only. A
// close by any other way calls history.back(), whose popstate is then the
// sheet's own unwinding, not a Back to act on.
//
// While an entry is held, the page's history restores no scroll position:
// going back to the page's own entry would otherwise put the page back where
// it was when the sheet opened, undoing a scroll the sheet's pick made, such
// as a category picked from the list. The page's own mode returns once every
// entry is gone.
//
// A plain module over a small window, so it is tested without a DOM.

export interface HistoryWindow {
	history: {
		pushState(data: unknown, unused: string): void;
		back(): void;
		scrollRestoration: 'auto' | 'manual';
	};
	addEventListener(type: 'popstate', listener: () => void): void;
	removeEventListener(type: 'popstate', listener: () => void): void;
}

/** Ends a hold that Back did not end: takes its entry back off. */
export type Release = () => void;

export interface SheetHistory {
	/** Holds one entry while a sheet is open; onBack runs when Back takes it. */
	hold(onBack: () => void): Release;
	/**
	 * Runs then once no entry is held and every release's history.back() has
	 * landed, so a page can leave from its own entry, never a sheet's.
	 */
	settled(then: () => void): void;
}

/** The marker of the entries a sheet holds. */
export const SHEET_ENTRY = 'bosla-sheet';

export function sheetHistory(win: HistoryWindow): SheetHistory {
	// The open holds, the newest last, and how many popstates are a release's
	// own history.back() still to arrive.
	const held: { onBack: () => void }[] = [];
	let unwinding = 0;
	let restoration: 'auto' | 'manual' = 'auto';
	let waiting: (() => void)[] = [];

	const start = () => {
		restoration = win.history.scrollRestoration;
		win.history.scrollRestoration = 'manual';
		win.addEventListener('popstate', onPop);
	};
	const finish = () => {
		if (held.length > 0 || unwinding > 0) return;
		win.removeEventListener('popstate', onPop);
		win.history.scrollRestoration = restoration;
		const then = waiting;
		waiting = [];
		for (const run of then) run();
	};

	function onPop() {
		if (unwinding > 0) {
			unwinding--;
			finish();
			return;
		}
		const top = held.pop();
		finish();
		top?.onBack();
	}

	return {
		hold(onBack) {
			const entry = { onBack };
			if (held.length === 0 && unwinding === 0) start();
			held.push(entry);
			win.history.pushState({ [SHEET_ENTRY]: true }, '');
			return () => {
				const at = held.indexOf(entry);
				// Back already took it.
				if (at < 0) return;
				held.splice(at, 1);
				unwinding++;
				win.history.back();
			};
		},
		settled(then) {
			if (held.length === 0 && unwinding === 0) then();
			else waiting.push(then);
		},
	};
}

let shared: SheetHistory | undefined;

/** Holds the page's history while a sheet is open; see sheetHistory. */
export function holdBack(onBack: () => void): Release {
	shared ??= sheetHistory(window);
	return shared.hold(onBack);
}

/**
 * Goes to an address from the page's own entry: once every sheet's entry is
 * off the history, so Back from there comes straight back to this page, at
 * its own scroll. A link inside a sheet that holds Back leaves this way.
 */
export function leaveTo(href: string): void {
	shared ??= sheetHistory(window);
	shared.settled(() => window.location.assign(href));
}
